import React, { useState, useRef, useEffect } from "react";
import "./ChatBotRCP.css";
import chatIcon from "../../assets/images/chat-icon-rcp.jpg";
import dapIcon from "../../assets/images/dapIcon.png";

const FloatingChatWidgetRCP = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("chatMessagesrcp");
    return saved
      ? JSON.parse(saved)
      : [{ type: "bot", text: "👋 Hi! I'm your CIEL Assistant. How can I help you today?" }];
  });
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const chatRef = useRef(null);
  const endOfMessagesRef = useRef(null);
  const currentReader = useRef(null);
  const currentAbortController = useRef(null);
  const isUnmounted = useRef(false);

  const emojiMap = {
    pricing: "💰",
    cost: "💰",
    planning: "🗓️",
    investment: "📈",
    job: "💼",
    duration: "⏳",
    hour: "⏱️",
    minute: "⏱️",
    audience: "🎯",
    objective: "🎯",
    training: "📅",
    location: "📍",
    default: "🔹",
    search: "🔍",
    link: "🔗",
    date: "🗓️",
    successfully:"✅",
    confirm:"✅",
    welcome:"✨",
  };

  const formatBotResponse = (text) => {
    if (!text) return [""];
  
    const lines = text.split("\n").filter(Boolean);
    const formatted = [];
    const useEmoji = lines.length >= 5;
  
    lines.forEach((line, idx) => {
      let trimmed = line.trim();
      trimmed = trimmed.replace(/,+$/, "");
      trimmed = trimmed.replace(/^[-•–]\s*/, "");
  
      // First line → use relevant emoji
      if (idx === 0) {
        let emoji = "";
        if (/locations/i.test(trimmed)) emoji = emojiMap.location;
        else if (/upcoming program dates/i.test(trimmed)) emoji = emojiMap.date;
        else if (/confirm/i.test(trimmed) || /please confirm/i.test(trimmed)) emoji = emojiMap.confirm;
        else if (/^you're welcome/i.test(trimmed)) emoji = emojiMap.welcome;
        else if (useEmoji) emoji = emojiMap.default;
  
        formatted.push(`${emoji} ${trimmed}`);
        return;
      }
  
      // Dates → 2 spaces + default emoji
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
        formatted.push(`  ${emojiMap.default} ${trimmed}`);
        return;
      }
  
      // Key-value lines → 2 spaces + default emoji
      if (/^[\w\s]+:/.test(trimmed)) {
        formatted.push(`  ${emojiMap.default} ${trimmed}`);
        return;
      }
  
      // Booking prompt → leave as is
      if (/would you like/i.test(trimmed)) {
        formatted.push(trimmed);
        return;
      }
  
      // Dynamic location detection → single or two-word line (not matching above)
      if (/^[A-Za-z\s]+$/.test(trimmed) && trimmed.split(" ").length <= 2) {
        formatted.push(`  ${emojiMap.default} ${trimmed}`);
        return;
      }
  
      // Fallback → 2 spaces + default emoji for any other line
      formatted.push(`  ${emojiMap.default} ${trimmed}`);
    });
  
    return formatted;
  };
  
  
  
  

  function generateSessionId() {
    return "session-" + Math.random().toString(36).substring(2, 15);
  }

  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("agentSessionIdRCP") || generateSessionId();
  });

  useEffect(() => {
    localStorage.setItem("agentSessionIdRCP", sessionId);
  }, [sessionId]);

  const cancelActiveStream = async () => {
    if (currentAbortController.current) {
      try { currentAbortController.current.abort(); } catch (_) {}
      currentAbortController.current = null;
    }
    if (currentReader.current) {
      try { await currentReader.current.cancel(); } catch (_) {}
      try { if (typeof currentReader.current.releaseLock === "function") currentReader.current.releaseLock(); } catch (_) {}
      currentReader.current = null;
    }
    setMessages((prev) => prev.filter((m) => m.type !== "bot-temp"));
  };

  const sendMessage = async (message, retry = false) => {
    if (!message.trim() || isTyping) return;

    if (message.trim().toLowerCase() === "clear") {
      await cancelActiveStream();
      const initialMsg = {
        type: "bot",
        text: "👋 Hi! I'm your CIEL Assistant. How can I help you today?",
      };
      setMessages([initialMsg]);
      localStorage.setItem("chatMessagesrcp", JSON.stringify([initialMsg]));
      setInput("");
      return;
    }

    await cancelActiveStream();
    if (!retry) setMessages((prev) => [...prev, { type: "user", text: message }]);
    setInput("");
    setIsTyping(true);

    const controller = new AbortController();
    currentAbortController.current = controller;

    try {
      let currentSessionId = localStorage.getItem("agentSessionIdRCP") || sessionId;
      const url = `https://8rhbl820k3.execute-api.ap-south-1.amazonaws.com/production/cpeapi?message=${encodeURIComponent(message)}&sessionId=${currentSessionId}`;

      const response = await fetch(url, { method: "GET", headers: { Accept: "text/plain, */*", "Cache-Control": "no-cache" }, signal: controller.signal });

      const newSessionId = response.headers.get("x-session-id");
      if (newSessionId && newSessionId !== currentSessionId) {
        localStorage.setItem("agentSessionIdRCP", newSessionId);
        setSessionId(newSessionId);
        currentSessionId = newSessionId;
      }

      if (response.status === 500) {
        if (!retry) {
          const freshSessionId = generateSessionId();
          localStorage.setItem("agentSessionIdRCP", freshSessionId);
          setSessionId(freshSessionId);
          return sendMessage(message, true);
        } else throw new Error("Repeated 500 error even after new session");
      }

      if (!response.ok) {
        let errText = "";
        try { errText = await response.json(); } catch (_) {}
        throw new Error(`HTTP ${response.status}: ${errText.response || "Request failed"}`);
      }

      const result = await response.json();
      const botMessage = result.response;
      if (botMessage && !isUnmounted.current) setMessages((prev) => [...prev, { type: "bot", text: botMessage }]);
    } catch (error) {
      console.error("Chat error:", error);
      if (!retry) return sendMessage(message, true);
      if (!isUnmounted.current) setMessages((prev) => [...prev, { type: "bot", text: "⚠️ Something went wrong. Please try again." }]);
    } finally {
      if (!isUnmounted.current) setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  useEffect(() => {
    if (endOfMessagesRef.current) endOfMessagesRef.current.scrollIntoView({ behavior: "smooth" });
    localStorage.setItem("chatMessagesrcp", JSON.stringify(messages));
  }, [messages, isTyping]);

  useEffect(() => {
    return () => {
      isUnmounted.current = true;
      if (currentAbortController.current) try { currentAbortController.current.abort(); } catch (_) {}
      if (currentReader.current) try { currentReader.current.cancel(); } catch (_) {}
    };
  }, []);

  return (
    <div className="chat-widget-wrapper-rcp">
      {!isOpen && (
        <button className="chat-toggle-button" onClick={() => setIsOpen(true)}>
          <img src={chatIcon} alt="Chat" />
        </button>
      )}

      {isOpen && (
        <div className="floating-chat-box">
          <div className="chat-header-rcp">
            <div className="chat-header-left">
              <img className="chat-icon" src={dapIcon} alt="Chat" />
              <span className="chat-title">CIEL AI Buddy – Your 24×7 Assistant</span>
            </div>
            <button className="close-btn" onClick={() => setIsOpen(false)}>×</button>
          </div>

          <div className="chat-body" ref={chatRef}>
            {messages.map((msg, idx) => {
              const content = msg.type === "bot" ? formatBotResponse(msg.text).join("<br/>") : msg.text;
              return <div key={idx} className={`message ${msg.type}`} dangerouslySetInnerHTML={{ __html: content }}></div>;
            })}
            {isTyping && <div className="message bot">Wait...</div>}
            <div ref={endOfMessagesRef} />
          </div>

          <div className="chat-input-area">
            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type a message..." rows={1} />
            <button onClick={() => sendMessage(input)} disabled={isTyping || !input.trim()}>➤</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingChatWidgetRCP;
